import React, { useState } from "react";
import { MapPin, Compass, Send, Trash2, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

const purposeLabel = { healing: "Healing", love: "Love", "career inspiration": "Career", "spiritual awakening": "Awakening", adventure: "Adventure", relaxation: "Relaxation" };
const timingLabel = { "3_months": "next 3 months", "6_months": "next 6 months", "12_months": "next 12 months" };

function CardHeader({ journey }) {
  const purpose = journey.inputs?.purpose || "";
  const timing = journey.inputs?.timing || "";
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#A89C98] flex items-center gap-1.5">
          <MapPin size={11} />
          {purposeLabel[purpose] || purpose || "Journey"}
          {timing && <span className="text-[#A89C98]/70">· {timingLabel[timing] || timing}</span>}
        </div>
        <div className="font-heading text-xl mt-1 truncate">{journey.destination?.city_display || journey.destination?.city}</div>
        {journey.destination?.country && <div className="text-xs text-[#A89C98] mt-0.5">{journey.destination.country}</div>}
      </div>
      <div className="text-right shrink-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#A89C98]">Budget</div>
        <div className="text-sm font-mono-scanner text-[#C8A96E] mt-0.5">{journey.budget?.display || "—"}</div>
      </div>
    </div>
  );
}

function CardDetails({ journey }) {
  return (
    <div className="mt-4 pt-4 border-t border-white/5 space-y-3" data-testid={`journey-card-details-${journey.journey_id}`}>
      <p className="text-xs text-[#E8DFD3] leading-relaxed italic">{journey.why}</p>
      {Array.isArray(journey.itinerary) && journey.itinerary.length > 0 && (
        <ol className="text-xs text-[#A89C98] space-y-1.5">
          {journey.itinerary.map((line) => (
            <li key={line} className="flex gap-2"><Compass size={10} className="text-[#C8A96E] mt-0.5 shrink-0" /><span>{line}</span></li>
          ))}
        </ol>
      )}
    </div>
  );
}

function ForwardButton({ journey, onForward }) {
  const [forwarding, setForwarding] = useState(false);
  const forwarded = !!journey.forwarded_to_lunamore_at;
  const handle = async () => {
    setForwarding(true);
    try {
      await api.post(`/journeys/${journey.journey_id}/forward`);
      toast.success("Sent to Lunamore concierge ✨");
      onForward(journey.journey_id);
    } catch {
      toast.error("Could not reach Lunamore — try again");
    } finally {
      setForwarding(false);
      window.open("https://lunamoretravel.com", "_blank", "noopener,noreferrer");
    }
  };
  const cls = forwarded ? "bg-transparent border border-[#9CBF8E]/60 text-[#9CBF8E] hover:bg-[#9CBF8E]/10" : "bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55]";
  return (
    <Button data-testid={`journey-forward-${journey.journey_id}`} onClick={handle} disabled={forwarding} className={`h-8 px-3 rounded-none uppercase tracking-widest text-[10px] font-bold flex items-center gap-1.5 ${cls}`}>
      {forwarded ? <><ExternalLink size={11} /> Reforward</> : <><Send size={11} /> Send to Lunamore</>}
    </Button>
  );
}

export function JourneyCard({ journey, onForward, onDelete }) {
  const [open, setOpen] = useState(false);
  const handleDelete = async () => {
    if (!window.confirm(`Remove your ${journey.destination?.city_display} journey?`)) return;
    try {
      await api.delete(`/journeys/${journey.journey_id}`);
      onDelete(journey.journey_id);
      toast.success("Journey removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="glass rounded-xl p-5 border border-white/5 hover:border-[#C8A96E]/40 transition-colors" data-testid={`journey-card-${journey.journey_id}`}>
      <CardHeader journey={journey} />
      {open && <CardDetails journey={journey} />}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button data-testid={`journey-toggle-${journey.journey_id}`} onClick={() => setOpen((o) => !o)} className="text-[10px] uppercase tracking-widest text-[#A89C98] hover:text-white transition-colors">
          {open ? "Hide details" : "View itinerary"}
        </button>
        <div className="ml-auto flex items-center gap-2">
          <ForwardButton journey={journey} onForward={onForward} />
          <button data-testid={`journey-delete-${journey.journey_id}`} onClick={handleDelete} className="w-8 h-8 rounded border border-white/10 text-[#A89C98] hover:text-[#EF4444] hover:border-[#EF4444]/40 transition-colors flex items-center justify-center" aria-label="Delete journey">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
